import { Position } from '@capacitor/geolocation';
import {
  ActionIcon,
  Box,
  Button,
  Center,
  CopyButton,
  Group,
  Loader,
  Modal,
  Paper,
  Progress,
  SegmentedControl,
  Select,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { bbox } from '@turf/bbox';
import { centerOfMass } from '@turf/center-of-mass';
import { distance } from '@turf/distance';
import { point } from '@turf/helpers';
import { BrowserMultiFormatReader } from '@zxing/library';
import clsx from 'clsx';
import {
  AlertCircle,
  Camera,
  Check,
  CheckCircle2,
  Copy,
  Flag,
  Info,
  Image as ImageIcon,
  MapPin,
  MessageSquare,
  Navigation,
  ScanBarcode,
  X as XIcon,
} from 'lucide-react';
import { LngLatBoundsLike, Map, MapEvent, MapMouseEvent } from 'mapbox-gl';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapRef } from 'react-map-gl';

import FieldsInBounds, {
  CLUSTER_LAYER_ID,
  FIELDS_LAYER_ID,
  INTERACTIVE_LAYER_IDS,
} from '../../components/fields-in-bounds/fields-in-bounds';

import { ErrorPage } from './page.error';
import styles from './page.module.css';
import { getPointDrawerMaxHeightPx } from './drawer-layout';
import { captureVideoFrameAsFile } from './capture-barcode-scan-image';

import { useQueriesWithGlobal } from '@data-access';
import {
  fieldsQueries,
  FIELDWORK_LOCKED_COLOR,
  flattenIlotSamples,
  formatSachetBarcodeDisplay,
  getImpossibleTitleLabel,
  isRequestTimeoutError,
  normalizeSachetBarcode,
  pointsApi,
  resolveMapPointScope,
  writeMapPointScope,
  type UpdatePointMapDto,
  type FlattenedIlotPointFeature,
} from '@fields/data-access';
import {
  GeolocateControl,
  IlotPointsLayer,
  PfMap,
  PlotOutlineLayer,
  StyleControl,
} from '@map';
import { BottomSheet, FullMainHeight } from '@ui';

export function FieldsMap() {
  const { t, i18n } = useTranslation();
  const search = useSearch({
    from: '/_app/map',
  });
  const navigate = useNavigate({
    from: '/map',
  });
  const scope = resolveMapPointScope(search.scope);
  useEffect(() => {
    writeMapPointScope(scope);
  }, [scope]);
  const mapRef = useRef<MapRef>(null);
  const restoredContextRef = useRef(false);
  const restoredPointIdRef = useRef<number | null>(null);
  const restoredPointCodeRef = useRef<string | null>(null);
  const restoredSampleIdRef = useRef<number | null>(null);
  const restoredViewportRef = useRef(false);
  const lastPointClickAtRef = useRef<number>(0);
  const [opened, actions] = useDisclosure();
  const [currentPosition, setCurrentPosition] = useState<Position | null>(null);
  const [selectedIlotId, setSelectedIlotId] = useState<number | null>(null);
  const [selectedSampleId, setSelectedSampleId] = useState<number | null>(
    null,
  );
  const [selectedPoint, setSelectedPoint] =
    useState<FlattenedIlotPointFeature | null>(null);
  const [selectedIlotFeature, setSelectedIlotFeature] = useState<GeoJSON.Feature<
    GeoJSON.MultiPolygon,
    Record<string, unknown>
  > | null>(null);
  const [identifiantValue, setIdentifiantValue] = useState('');
  const [stoninessValue, setStoninessValue] = useState<string | null>(
    null,
  );
  const [pointUpdateError, setPointUpdateError] = useState<string | null>(
    null,
  );
  const [allowValidateWithoutPhoto, setAllowValidateWithoutPhoto] =
    useState(false);
  const [missingPhotoConfirmOpened, setMissingPhotoConfirmOpened] =
    useState(false);
  const [stoninessImageModalOpened, setStoninessImageModalOpened] =
    useState(false);
  const [stoninessImageModalUrl, setStoninessImageModalUrl] =
    useState<string | null>(null);
  const [stoninessImageModalLoading, setStoninessImageModalLoading] =
    useState(false);
  const [measuredDrawerHeight, setMeasuredDrawerHeight] = useState(0);
  const [isScanningBarcode, setIsScanningBarcode] = useState(false);
  const [barcodeScanError, setBarcodeScanError] = useState<string | null>(null);
  const [pendingBarcodeValue, setPendingBarcodeValue] = useState<string | null>(
    null,
  );
  const [pendingBarcodeImage, setPendingBarcodeImage] = useState<File | null>(
    null,
  );
  const [isConfirmingBarcode, setIsConfirmingBarcode] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const readerRef = useRef<BrowserMultiFormatReader | null>(null);
  const isScanningRef = useRef(false);
  const stoninessImageInputRef = useRef<HTMLInputElement | null>(null);

  const identifiantDebounceTimerRef = useRef<
    ReturnType<typeof setTimeout> | null
  >(null);
  const lastPatchedSachetIdRef = useRef<string>('');
  const lastPatchedStoninessRef = useRef<string | null>(null);

  // Keep latest form values for effects that should only re-run on one field change.
  const identifiantValueRef = useRef<string>('');
  const stoninessValueRef = useRef<string | null>(null);

  // Prevent re-sending the same failing PATCH payload.
  // If backend returns 500, we show the error but we do not retry automatically
  // until the user changes the relevant inputs.
  const lastAttemptSachetPatchKeyRef = useRef<string>('');
  const lastAttemptStoninessPatchKeyRef = useRef<string>('');

  const ilotCenter = useMemo(() => {
    if (!selectedIlotFeature) {
      return null;
    }

    try {
      // Turf uses [lng, lat] ordering for GeoJSON coordinates.
      const { geometry } = centerOfMass(selectedIlotFeature);
      const [lng, lat] = geometry.coordinates;
      return { lat, lng };
    } catch {
      return null;
    }
  }, [selectedIlotFeature]);

  const queries = useQueriesWithGlobal(fieldsQueries);
  const queryClient = useQueryClient();
  const ilotDetail = useQuery({
    ...queries.ilotDetail(selectedIlotId!, scope),
    enabled: selectedIlotId !== null,
  });
  const refreshMapAfterPointUpdate = useCallback(() => {
    void ilotDetail.refetch();
    void queryClient.invalidateQueries({
      queryKey: queries.bounded(),
    });
  }, [ilotDetail, queryClient, queries]);
  const flattenedPoints = useMemo(
    () =>
      ilotDetail.data?.samples?.length
        ? flattenIlotSamples(ilotDetail.data.samples)
        : [],
    [ilotDetail.data?.samples],
  );

  const maxBounds = useQuery(queries.maxBounds(scope));

  const pointsStoninessValues = useQuery({
    queryKey: ['points', 'values', i18n.language],
    queryFn: () => pointsApi.getPointsValues(),
  });

  const reportPointError = useCallback(
    (error: unknown, fallback: string) => {
      if (isRequestTimeoutError(error)) {
        setAllowValidateWithoutPhoto(true);
        setPointUpdateError(t('sampling.map.errors.requestTimeout'));
        return;
      }
      setPointUpdateError(fallback);
    },
    [t],
  );

  const updatePointMutation = useMutation({
    mutationFn: ({
      pointId,
      data,
    }: {
      pointId: number;
      data: UpdatePointMapDto;
    }) => pointsApi.patchPoint(pointId, data, scope),
    retry: 0,
  });

  const uploadStoninessImageMutation = useMutation({
    mutationFn: ({
      pointId,
      file,
      sachetId,
      stoniness,
    }: {
      pointId: number;
      file: File;
      sachetId: string | null;
      stoniness: string | null;
    }) =>
      pointsApi.patchPointStoninessImage({
        id: pointId,
        file,
        sachet_id: sachetId,
        stoniness,
        scope,
      }),
    retry: 0,
  });

  const uploadSachetBarcodeMutation = useMutation({
    mutationFn: ({
      pointId,
      sachetId,
      file,
    }: {
      pointId: number;
      sachetId: string;
      file: File;
    }) =>
      pointsApi.patchPointSachet({
        id: pointId,
        sachet_id: sachetId,
        file,
        scope,
      }),
    retry: 0,
  });

  const validatePointMutation = useMutation({
    mutationFn: ({
      pointId,
      sachetId,
      stoniness,
    }: {
      pointId: number;
      sachetId: string | null;
      stoniness: string | null;
    }) =>
      pointsApi.patchPoint(
        pointId,
        {
          sachet_id: sachetId,
          stoniness,
          is_sampled: true,
        },
        scope,
      ),
    retry: 0,
    onSuccess: () => {
      setPointUpdateError(null);
      setAllowValidateWithoutPhoto(false);
      setMissingPhotoConfirmOpened(false);
      refreshMapAfterPointUpdate();
    },
    onError: (err) => {
      const fallback = err instanceof Error ? err.message : String(err);
      reportPointError(err, fallback);
    },
  });

  useEffect(() => {
    identifiantValueRef.current = identifiantValue;
  }, [identifiantValue]);

  useEffect(() => {
    stoninessValueRef.current = stoninessValue;
  }, [stoninessValue]);

  const proximityMeters = useMemo(() => {
    if (!currentPosition || !selectedPoint?.geometry?.coordinates) {
      return null;
    }

    // selectedPoint geometry is [lng, lat] (see IlotPointsLayer).
    const [pointLng, pointLat] = selectedPoint.geometry
      .coordinates as [number, number];
    const userLat = currentPosition.coords.latitude;
    const userLng = currentPosition.coords.longitude;

    return distance(point([userLng, userLat]), point([pointLng, pointLat]), {
      units: 'meters',
    });
  }, [currentPosition, selectedPoint]);

  const proximityUi = useMemo(() => {
    // Linear mapping: 0m => 100%, max(100m) => 0%.
    const meters = proximityMeters ?? null;
    const clamped = meters === null ? null : Math.max(0, Math.min(100, meters));
    const value =
      clamped === null ? 0 : Math.round((1 - clamped / 100) * 100);

    const color =
      clamped === null
        ? '#adb5bd'
        : clamped <= 20
          ? '#40c057'
          : clamped <= 50
            ? '#f59f00'
            : '#e03131';

    return { value, color };
  }, [proximityMeters]);

  const selectedPointApiId = useMemo(() => {
    if (selectedPoint?.id === undefined || selectedPoint?.id === null) {
      return null;
    }
    if (typeof selectedPoint.id === 'number') return selectedPoint.id;

    const asNumber = Number(selectedPoint.id);
    if (!Number.isFinite(asNumber)) return null;
    return asNumber;
  }, [selectedPoint]);

  // When ilotDetail is refetched (after PATCH), keep `selectedPoint` in sync
  // so fields like `stoniness_image` update without requiring a re-click.
  useEffect(() => {
    if (!selectedPointApiId) return;
    const updated = flattenedPoints.find((p) => {
      if (p.id === undefined || p.id === null) return false;
      return Number(p.id) === selectedPointApiId;
    });

    if (updated && updated !== selectedPoint) {
      setSelectedPoint(updated);
    }
  }, [flattenedPoints, selectedPointApiId, selectedPoint]);

  const getBackendFileUrl = useCallback(
    (maybePath: string | null) => {
      if (!maybePath) return null;
      if (maybePath.startsWith('http://') || maybePath.startsWith('https://')) {
        return maybePath;
      }

      const apiBase = import.meta.env.VITE_API_URL;
      // `VITE_API_URL` is used as `/api/` prefix for JSON endpoints.
      // File URLs returned by backend are often served from the backend root.
      const backendBase = apiBase.replace(/\/api\/?$/, '');
      const normalized = maybePath.startsWith('/')
        ? maybePath
        : `/${maybePath}`;

      return `${backendBase}${normalized}`;
    },
    [],
  );

  const stoninessImagePath =
    selectedPoint?.properties.stoniness_image ?? null;
  const stoninessImageUrl = getBackendFileUrl(stoninessImagePath);
  const isStoninessImageUploaded = Boolean(stoninessImagePath);

  const hasIdentifiantValue = identifiantValue.trim().length > 0;
  const isSampled = selectedPoint?.properties.is_sampled ?? false;
  const isImpossible = selectedPoint?.properties.is_impossible ?? false;
  const isCloseEnough =
    proximityMeters !== null && proximityMeters !== undefined
      ? proximityMeters <= 20
      : false;
  const hasStoniness = stoninessValue !== null;
  const fieldworkAllowed = selectedPoint?.properties.fieldwork_allowed !== false;
  const canValidatePoint =
    fieldworkAllowed &&
    !isSampled &&
    !isImpossible &&
    hasStoniness &&
    (isStoninessImageUploaded || allowValidateWithoutPhoto) &&
    isCloseEnough;
  const showValidationHint = isSampled || isImpossible || !canValidatePoint;
  const validationHint = useMemo(() => {
    if (isSampled) return t('sampling.map.validation.alreadyValidated');
    if (isImpossible) return t('sampling.map.validation.markedImpossible');
    if (!fieldworkAllowed) {
      return t('sampling.map.validation.organicPending');
    }
    if (!hasStoniness) return t('sampling.map.validation.fillStoniness');
    if (!isStoninessImageUploaded && !allowValidateWithoutPhoto) {
      return t('sampling.map.validation.addPhoto');
    }
    if (!isCloseEnough) return t('sampling.map.validation.getCloser');
    return t('sampling.map.validation.allReady');
  }, [
    isSampled,
    isImpossible,
    fieldworkAllowed,
    hasStoniness,
    isStoninessImageUploaded,
    allowValidateWithoutPhoto,
    isCloseEnough,
    t,
  ]);

  const submitPointValidation = useCallback(() => {
    if (!selectedPointApiId) return;
    setMissingPhotoConfirmOpened(false);
    validatePointMutation.mutate({
      pointId: selectedPointApiId,
      sachetId: identifiantValue.trim().length ? identifiantValue : null,
      stoniness: stoninessValue,
    });
  }, [
    identifiantValue,
    selectedPointApiId,
    stoninessValue,
    validatePointMutation,
  ]);

  const handleValidatePointClick = useCallback(() => {
    if (!selectedPointApiId || !canValidatePoint) return;
    if (!isStoninessImageUploaded) {
      setMissingPhotoConfirmOpened(true);
      return;
    }
    submitPointValidation();
  }, [
    canValidatePoint,
    isStoninessImageUploaded,
    selectedPointApiId,
    submitPointValidation,
  ]);
  const impossibleTitleLabel = useMemo(
    () =>
      getImpossibleTitleLabel(
        selectedPoint?.properties.impossible_title,
        pointsStoninessValues.data?.title,
      ),
    [
      pointsStoninessValues.data?.title,
      selectedPoint?.properties.impossible_title,
    ],
  );

  const selectedIlotOutlineColor = useMemo(() => {
    const ilotProps = selectedIlotFeature?.properties as {
      fieldwork_allowed?: boolean;
    } | undefined;
    if (ilotProps?.fieldwork_allowed === false) {
      const parcelColor = (ilotProps as { color?: string }).color;
      return parcelColor || FIELDWORK_LOCKED_COLOR;
    }
    if (selectedPoint && !fieldworkAllowed) {
      return FIELDWORK_LOCKED_COLOR;
    }
    return '#e03131';
  }, [selectedIlotFeature, selectedPoint, fieldworkAllowed]);

  const handleMapClick = (e: MapMouseEvent) => {
    const feature = e.features?.[0];

    if (!feature) {
      return;
    }

    // Marker clicks can sometimes also trigger the map onClick (canvas) underneath.
    // If that happens right after a point click, ignore it so we don't reset the
    // selected sample/point.
    if (
      feature.layer?.id === FIELDS_LAYER_ID &&
      Date.now() - lastPointClickAtRef.current < 250
    ) {
      return;
    }

    switch (feature.layer?.id) {
      case FIELDS_LAYER_ID: {
        if (feature.id) {
          if (feature.properties?.fieldwork_allowed === false) {
            return;
          }
          const id = feature.id as number;
          // Re-clicking the same ilot boundary clears the selected point and
          // triggers auto-select of the first point — ignore when a point is
          // already selected on this ilot. Clicking a different ilot still works.
          if (id === selectedIlotId && selectedPoint) {
            return;
          }
          setSelectedSampleId(null);
          setSelectedPoint(null);
          setSelectedIlotId(id);
          setSelectedIlotFeature(
            feature as GeoJSON.Feature<
              GeoJSON.MultiPolygon,
              Record<string, unknown>
            >,
          );
          actions.open();
        }
        break;
      }
      case CLUSTER_LAYER_ID:
      case `${CLUSTER_LAYER_ID}_points`: {
        const [x1, y1, x2, y2] =
          JSON.parse(feature.properties?.bbox) || bbox(feature.geometry);
        e.target.fitBounds([x1, y1, x2, y2], {
          padding: 100,
          animate: false,
        });
        break;
      }
    }
  };

  const handlePositionChange = (position: Position) => {
    setCurrentPosition(position);
  };

  const handleClose = useCallback(() => {
    actions.close();
    setSelectedIlotId(null);
    setSelectedIlotFeature(null);
    setSelectedSampleId(null);
    setSelectedPoint(null);
    setIdentifiantValue('');
    setStoninessValue(null);
    setPointUpdateError(null);
    setAllowValidateWithoutPhoto(false);
    setMissingPhotoConfirmOpened(false);
    setBarcodeScanError(null);
    setPendingBarcodeValue(null);
    setPendingBarcodeImage(null);
    isScanningRef.current = false;

    if (identifiantDebounceTimerRef.current) {
      clearTimeout(identifiantDebounceTimerRef.current);
      identifiantDebounceTimerRef.current = null;
    }

    if (readerRef.current) {
      try {
        readerRef.current.stopContinuousDecode();
      } catch {
        // ignore
      }

      try {
        readerRef.current.reset();
      } catch {
        // ignore
      }
    }

    readerRef.current = null;

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((t) => t.stop());
    }

    mediaStreamRef.current = null;
    setIsScanningBarcode(false);
  }, [actions]);

  const handleScopeChange = useCallback(
    (nextScope: string) => {
      if (nextScope !== 'personal' && nextScope !== 'organization') return;
      if (nextScope === scope) return;
      writeMapPointScope(nextScope);
      handleClose();
      navigate({
        to: '/map',
        search: { scope: nextScope, fitBounds: Date.now() },
      });
    },
    [handleClose, navigate, scope],
  );

  const handlePointClick = useCallback(
    (feature: FlattenedIlotPointFeature) => {
      if (!feature.properties.fieldwork_allowed) {
        return;
      }
      lastPointClickAtRef.current = Date.now();
      setSelectedSampleId(feature.properties.sampleId);
      setSelectedPoint(feature);

      const sachetId = feature.properties.sachet_id ?? '';
      const stoniness = feature.properties.stoniness ?? null;
      lastPatchedSachetIdRef.current = sachetId;
      lastPatchedStoninessRef.current = stoniness;

      setIdentifiantValue(sachetId);
      setStoninessValue(stoniness);
      setPointUpdateError(null);
      setAllowValidateWithoutPhoto(false);
      setMissingPhotoConfirmOpened(false);
      setBarcodeScanError(null);
      setPendingBarcodeValue(null);
      setPendingBarcodeImage(null);
      actions.open();
    },
    [actions],
  );

  const openStoninessImagePicker = useCallback(() => {
    stoninessImageInputRef.current?.click();
  }, []);

  const handleStoninessImageFileChange = useCallback(
    (e: { currentTarget: HTMLInputElement }) => {
      const input = e.currentTarget;
      const file = input.files?.[0];
      if (!file) return;
      if (!selectedPointApiId) return;
      if (!isCloseEnough) return;
      if (uploadStoninessImageMutation.isPending) return;

      const sachetIdToSend =
        identifiantValueRef.current.trim().length > 0
          ? identifiantValueRef.current
          : null;
      const stoninessToSend = stoninessValueRef.current;

      setPointUpdateError(null);
      void uploadStoninessImageMutation
        .mutateAsync({
          pointId: selectedPointApiId,
          file,
          sachetId: sachetIdToSend,
          stoniness: stoninessToSend,
        })
        .then(() => {
          setAllowValidateWithoutPhoto(false);
          refreshMapAfterPointUpdate();
        })
        .catch((err) => {
          reportPointError(err, t('sampling.map.errors.stoninessPhotoUpload'));
        })
        .finally(() => {
          // currentTarget is null once the change event ends.
          input.value = '';
        });
    },
    [
      isCloseEnough,
      refreshMapAfterPointUpdate,
      reportPointError,
      selectedPointApiId,
      t,
      uploadStoninessImageMutation,
    ],
  );

  useEffect(() => {
    if (!selectedPointApiId) return;
    if (isImpossible) return;
    if (updatePointMutation.isPending) return;
    if (identifiantValue === lastPatchedSachetIdRef.current) return;

    if (identifiantDebounceTimerRef.current) {
      clearTimeout(identifiantDebounceTimerRef.current);
    }

    identifiantDebounceTimerRef.current = setTimeout(() => {
      const sachetIdToSend = identifiantValue;

      const patchKey = `sachet:${sachetIdToSend}`;
      if (patchKey === lastAttemptSachetPatchKeyRef.current) {
        return;
      }
      lastAttemptSachetPatchKeyRef.current = patchKey;

      void updatePointMutation
        .mutateAsync({
          pointId: selectedPointApiId,
          data: {
            sachet_id: sachetIdToSend,
          },
        })
        .then(() => {
          lastPatchedSachetIdRef.current = sachetIdToSend;
          setPointUpdateError(null);
          refreshMapAfterPointUpdate();
        })
        .catch((err) => {
          const rawMessage =
            err instanceof Error ? err.message : String(err);
          let readableMessage = rawMessage;

          // Backend can return: {"sachet_id":"..."}.
          // Show only the human content, not the raw JSON.
          try {
            const parsed = JSON.parse(rawMessage);
            if (
              parsed &&
              typeof parsed === 'object' &&
              'sachet_id' in parsed &&
              typeof parsed.sachet_id === 'string'
            ) {
              readableMessage = parsed.sachet_id;
            }
          } catch {
            // ignore (not JSON)
          }

          reportPointError(err, readableMessage);
        });
    }, 400);

    return () => {
      if (identifiantDebounceTimerRef.current) {
        clearTimeout(identifiantDebounceTimerRef.current);
      }
      identifiantDebounceTimerRef.current = null;
    };
  }, [
    identifiantValue,
    isImpossible,
    reportPointError,
    selectedPointApiId,
    updatePointMutation,
    refreshMapAfterPointUpdate,
  ]);

  useEffect(() => {
    if (!selectedPointApiId) return;
    if (isImpossible) return;
    if (!isCloseEnough) return;
    if (updatePointMutation.isPending) return;
    if (stoninessValue === lastPatchedStoninessRef.current) return;

    const stoninessToSend = stoninessValue;

    const patchKey = `stoniness:${stoninessToSend}`;
    if (patchKey === lastAttemptStoninessPatchKeyRef.current) {
      return;
    }
    lastAttemptStoninessPatchKeyRef.current = patchKey;

    void updatePointMutation
      .mutateAsync({
        pointId: selectedPointApiId,
        data: {
          stoniness: stoninessToSend,
        },
      })
      .then(() => {
        lastPatchedStoninessRef.current = stoninessToSend;
        setPointUpdateError(null);
        refreshMapAfterPointUpdate();
      })
      .catch((err) => {
        reportPointError(err, t('sampling.map.errors.stoninessUpdate'));
      });
  }, [
    isCloseEnough,
    isImpossible,
    reportPointError,
    selectedPointApiId,
    stoninessValue,
    t,
    updatePointMutation,
    refreshMapAfterPointUpdate,
  ]);

  const stopBarcodeScan = useCallback(() => {
    isScanningRef.current = false;

    if (readerRef.current) {
      try {
        readerRef.current.stopContinuousDecode();
      } catch {
        // ignore
      }

      try {
        readerRef.current.reset();
      } catch {
        // ignore
      }
    }

    readerRef.current = null;

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((t) => t.stop());
    }
    mediaStreamRef.current = null;

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setIsScanningBarcode(false);
  }, []);

  const handleCancelBarcodeConfirm = useCallback(() => {
    setPendingBarcodeValue(null);
    setPendingBarcodeImage(null);
  }, []);

  const handleConfirmBarcodeScan = useCallback(() => {
    if (!pendingBarcodeValue) {
      return;
    }

    const sachetId = normalizeSachetBarcode(pendingBarcodeValue);

    if (pendingBarcodeImage && selectedPointApiId) {
      setIsConfirmingBarcode(true);

      void uploadSachetBarcodeMutation
        .mutateAsync({
          pointId: selectedPointApiId,
          sachetId,
          file: pendingBarcodeImage,
        })
        .then(() => {
          setIdentifiantValue(sachetId);
          lastPatchedSachetIdRef.current = sachetId;
          setPointUpdateError(null);
          setPendingBarcodeValue(null);
          setPendingBarcodeImage(null);
          refreshMapAfterPointUpdate();
        })
        .catch((err) => {
          const rawMessage =
            err instanceof Error ? err.message : String(err);
          let readableMessage = rawMessage;

          try {
            const parsed = JSON.parse(rawMessage);
            if (
              parsed &&
              typeof parsed === 'object' &&
              'sachet_id' in parsed &&
              typeof parsed.sachet_id === 'string'
            ) {
              readableMessage = parsed.sachet_id;
            }
          } catch {
            // ignore (not JSON)
          }

          reportPointError(err, readableMessage);
        })
        .finally(() => {
          setIsConfirmingBarcode(false);
        });

      return;
    }

    setIdentifiantValue(sachetId);
    setPendingBarcodeValue(null);
    setPendingBarcodeImage(null);
  }, [
    pendingBarcodeImage,
    pendingBarcodeValue,
    refreshMapAfterPointUpdate,
    reportPointError,
    selectedPointApiId,
    uploadSachetBarcodeMutation,
  ]);

  const startBarcodeScan = useCallback(async () => {
    if (!selectedPoint) {
      return;
    }
    if (isScanningRef.current) {
      return;
    }

    setBarcodeScanError(null);

    if (!navigator.mediaDevices?.getUserMedia) {
      setBarcodeScanError(t('sampling.map.errors.cameraUnavailable'));
      return;
    }

    try {
      isScanningRef.current = true;
      setIsScanningBarcode(true);

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: { ideal: 'environment' } },
      });
      mediaStreamRef.current = stream;

      if (!videoRef.current) {
        // Wait one frame for the <video> element to mount (it depends on isScanningBarcode).
        await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
      }

      if (!videoRef.current) {
        throw new Error('Video element not ready');
      }

      videoRef.current.srcObject = stream;
      await videoRef.current.play();

      const codeReader = new BrowserMultiFormatReader();
      readerRef.current = codeReader;

      // Start continuous decoding from the provided stream.
      void codeReader
        .decodeFromStream(
          stream,
          videoRef.current,
          (result: any) => {
            if (!isScanningRef.current) {
              return;
            }

            const text = result?.getText?.()?.trim();
            if (text) {
              const video = videoRef.current;
              void (video
                ? captureVideoFrameAsFile(video)
                : Promise.resolve(null)
              ).then((file) => {
                stopBarcodeScan();
                setPendingBarcodeValue(text);
                setPendingBarcodeImage(file);
              });
            }
          },
        )
        .catch(() => {
          // Ignore decode errors; the reader will keep trying.
        });
    } catch {
      isScanningRef.current = false;
      setIsScanningBarcode(false);
      setBarcodeScanError(t('sampling.map.errors.cameraStart'));
    }
  }, [selectedPoint, stopBarcodeScan]);

  const handleRescanBarcode = useCallback(() => {
    setPendingBarcodeValue(null);
    setPendingBarcodeImage(null);
    void startBarcodeScan();
  }, [startBarcodeScan]);

  useEffect(() => {
    return () => {
      stopBarcodeScan();
    };
  }, [stopBarcodeScan]);

  const handleOpenTravelPlan = useCallback(() => {
    if (!selectedPoint?.geometry?.coordinates) {
      return;
    }

    // selectedPoint geometry is [lng, lat] (see IlotPointsLayer).
    const [lng, lat] = selectedPoint.geometry.coordinates as [number, number];

    const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`;
    // On iOS Safari, `window.open(..., '_blank')` can create a "ghost"/blank
    // web view after closing Maps and returning to the app. Opening Apple
    // Maps instead avoids that behavior.
    const isIOS = /iPad|iPhone|iPod/i.test(navigator.userAgent);
    if (isIOS) {
      const appleMapsUrl = `https://maps.apple.com/?daddr=${lat},${lng}&dirflg=d`;
      window.location.href = appleMapsUrl;
      return;
    }

    // Non-iOS: keep opening a separate window, but on Capacitor `_system`
    // routes the URL to the native browser.
    const isCapacitorNative = Boolean((window as any)?.Capacitor?.isNative);
    window.open(
      googleMapsUrl,
      isCapacitorNative ? '_system' : '_blank',
      'noopener,noreferrer',
    );
  }, [selectedPoint]);

  const handleOpenIlotInfo = useCallback(() => {
    if (!selectedIlotId) return;
    const map = mapRef.current?.getMap();
    const center = map?.getCenter();
    const zoom = map?.getZoom();

    navigate({
      to: '/map-infos',
      search: {
        ilotId: selectedIlotId,
        pointId: selectedPointApiId ?? undefined,
        pointCode: selectedPoint?.properties.code,
        sampleId: selectedSampleId ?? undefined,
        lng: center?.lng,
        lat: center?.lat,
        zoom,
        scope,
      },
    });
  }, [navigate, scope, selectedIlotId, selectedPoint, selectedPointApiId, selectedSampleId]);

  const handleOpenPointNotes = useCallback(() => {
    if (!selectedIlotId || !selectedPointApiId) return;
    const map = mapRef.current?.getMap();
    const center = map?.getCenter();
    const zoom = map?.getZoom();

    navigate({
      to: '/map-notes',
      search: {
        ilotId: selectedIlotId,
        pointId: selectedPointApiId,
        pointCode: selectedPoint?.properties.code,
        sampleId: selectedSampleId ?? undefined,
        lng: center?.lng,
        lat: center?.lat,
        zoom,
        scope,
      },
    });
  }, [navigate, scope, selectedIlotId, selectedPoint, selectedPointApiId, selectedSampleId]);

  const handleOpenPointImpossible = useCallback(() => {
    if (!selectedIlotId || !selectedPointApiId) return;
    const map = mapRef.current?.getMap();
    const center = map?.getCenter();
    const zoom = map?.getZoom();

    navigate({
      to: '/map-impossible',
      search: {
        ilotId: selectedIlotId,
        pointId: selectedPointApiId,
        pointCode: selectedPoint?.properties.code,
        sampleId: selectedSampleId ?? undefined,
        lng: center?.lng,
        lat: center?.lat,
        zoom,
        scope,
      },
    });
  }, [navigate, scope, selectedIlotId, selectedPoint, selectedPointApiId, selectedSampleId]);

  const zoomOnBbox = useCallback(
    (m: Map) => {
      if (search?.bbox) {
        m.fitBounds(search.bbox as LngLatBoundsLike, {
          padding: 100,
          animate: false,
        });
        navigate({
          to: '/map',
          search: { scope },
        });
      }
    },
    [navigate, scope, search],
  );

  const fitMaxBounds = useCallback(
    (m: Map) => {
      if (maxBounds.data?.bbox && m) {
        m.fitBounds(maxBounds.data.bbox, {
          padding: 20,
        });
      }
    },
    [maxBounds.data],
  );

  const applyFitBoundsRequest = useCallback(
    (map?: Map) => {
      const targetMap = map ?? mapRef.current?.getMap();
      if (!targetMap || !maxBounds.data?.bbox) {
        return false;
      }

      handleClose();
      fitMaxBounds(targetMap);
      navigate({
        to: '/map',
        search: { scope },
        replace: true,
      });
      return true;
    },
    [fitMaxBounds, handleClose, maxBounds.data?.bbox, navigate, scope],
  );

  const handleLoad = (e: MapEvent) => {
    if (
      !restoredViewportRef.current &&
      search.lng !== undefined &&
      search.lat !== undefined &&
      search.zoom !== undefined
    ) {
      restoredViewportRef.current = true;
      e.target.jumpTo({
        center: [search.lng, search.lat],
        zoom: search.zoom,
      });
      return;
    }

    if (search.fitBounds !== undefined) {
      if (applyFitBoundsRequest(e.target)) {
        return;
      }
    }

    const zoom = e.target.getZoom();
    if (search.bbox) {
      zoomOnBbox(e.target);
    } else if (zoom === 6) {
      fitMaxBounds(e.target);
    }
  };

  useEffect(() => {
    if (search.fitBounds === undefined) {
      return;
    }

    applyFitBoundsRequest();
  }, [applyFitBoundsRequest, search.fitBounds]);

  useEffect(() => {
    if (
      search.lng !== undefined &&
      search.lat !== undefined &&
      search.zoom !== undefined &&
      !restoredViewportRef.current
    ) {
      return;
    }
    if (mapRef.current) {
      const zoom = mapRef.current.getMap().getZoom();
      if (search.bbox) {
        zoomOnBbox(mapRef.current.getMap());
      } else if (zoom === 6) {
        fitMaxBounds(mapRef.current.getMap());
      }
    }
  }, [fitMaxBounds, search, zoomOnBbox]);

  useEffect(() => {
    if (restoredViewportRef.current) return;
    if (search.lng === undefined || search.lat === undefined || search.zoom === undefined) {
      return;
    }
    if (!mapRef.current) return;

    restoredViewportRef.current = true;
    mapRef.current.getMap().jumpTo({
      center: [search.lng, search.lat],
      zoom: search.zoom,
    });
  }, [search.lat, search.lng, search.zoom]);

  useEffect(() => {
    if (restoredContextRef.current) return;
    if (!search.ilotId) return;

    restoredContextRef.current = true;
    restoredPointIdRef.current = search.pointId ?? null;
    restoredPointCodeRef.current = search.pointCode ?? null;
    restoredSampleIdRef.current = search.sampleId ?? null;
    setSelectedIlotId(search.ilotId);
    actions.open();
    void queryClient.invalidateQueries({
      queryKey: queries.bounded(),
    });

    navigate({
      to: '/map',
      search: {
        scope,
        bbox: search.bbox,
        lng: search.lng,
        lat: search.lat,
        zoom: search.zoom,
      },
      replace: true,
    });
  }, [
    actions,
    navigate,
    queryClient,
    queries,
    scope,
    search.bbox,
    search.ilotId,
    search.lat,
    search.lng,
    search.pointId,
    search.pointCode,
    search.sampleId,
    search.zoom,
  ]);

  useEffect(() => {
    const hasPointRestoreTarget = Boolean(
      restoredPointIdRef.current ||
        restoredPointCodeRef.current ||
        restoredSampleIdRef.current,
    );
    if (!hasPointRestoreTarget) return;
    if (!flattenedPoints.length) return;

    const restoredPoint = flattenedPoints.find((p) => {
      const byId =
        restoredPointIdRef.current !== null &&
        Number(p.id) === restoredPointIdRef.current;
      const byCode =
        restoredPointCodeRef.current !== null &&
        p.properties.code === restoredPointCodeRef.current;
      const bySample =
        restoredSampleIdRef.current !== null &&
        p.properties.sampleId === restoredSampleIdRef.current;

      if (byId) return true;
      if (byCode && bySample) return true;
      if (byCode && restoredSampleIdRef.current === null) return true;
      return false;
    });
    if (!restoredPoint) return;

    setSelectedSampleId(restoredPoint.properties.sampleId);
    setSelectedPoint(restoredPoint);
    const hasSavedViewport =
      search.lng !== undefined &&
      search.lat !== undefined &&
      search.zoom !== undefined;

    // Keep user's original camera when we have an explicit saved viewport.
    // Only fallback to point-centering when no viewport could be restored.
    if (!hasSavedViewport) {
      const [lng, lat] = restoredPoint.geometry.coordinates as [number, number];
      mapRef.current?.getMap().easeTo({
        center: [lng, lat],
        zoom: Math.max(mapRef.current?.getMap().getZoom() ?? 0, 16),
        duration: 450,
      });
    }
    restoredPointIdRef.current = null;
    restoredPointCodeRef.current = null;
    restoredSampleIdRef.current = null;
  }, [flattenedPoints, search.lat, search.lng, search.zoom]);

  useEffect(() => {
    if (!opened) return;
    if (selectedIlotId === null) return;
    if (selectedPoint) return;
    if (!flattenedPoints.length) return;

    const hasPointRestoreTarget = Boolean(
      restoredPointIdRef.current ||
        restoredPointCodeRef.current ||
        restoredSampleIdRef.current,
    );
    if (hasPointRestoreTarget) return;

    const firstPoint =
      flattenedPoints.find((p) => p.properties.fieldwork_allowed) ?? null;
    if (!firstPoint) {
      return;
    }
    const sachetId = firstPoint.properties.sachet_id ?? '';
    const stoniness = firstPoint.properties.stoniness ?? null;

    setSelectedSampleId(firstPoint.properties.sampleId);
    setSelectedPoint(firstPoint);
    lastPatchedSachetIdRef.current = sachetId;
    lastPatchedStoninessRef.current = stoniness;
    setIdentifiantValue(sachetId);
    setStoninessValue(stoniness);
    setPointUpdateError(null);
    setBarcodeScanError(null);
    setPendingBarcodeValue(null);
    setPendingBarcodeImage(null);
  }, [flattenedPoints, opened, selectedIlotId, selectedPoint]);

  useEffect(() => {
    if (!opened || !selectedPoint) {
      setMeasuredDrawerHeight(0);
      return;
    }

    let observer: ResizeObserver | null = null;
    let raf = 0;

    const attach = () => {
      const host = document.getElementById('fields-map-container');
      const drawerEl = host?.querySelector(
        `.${styles.drawerContent}`,
      ) as HTMLElement | null;

      if (!drawerEl) {
        raf = requestAnimationFrame(attach);
        return;
      }

      const updateHeight = () => {
        setMeasuredDrawerHeight(drawerEl.getBoundingClientRect().height);
      };

      updateHeight();
      observer = new ResizeObserver(updateHeight);
      observer.observe(drawerEl);
    };

    attach();

    return () => {
      cancelAnimationFrame(raf);
      observer?.disconnect();
    };
  }, [
    opened,
    selectedPoint,
    showValidationHint,
    pointUpdateError,
    barcodeScanError,
    isImpossible,
    uploadStoninessImageMutation.isPending,
  ]);

  // When the sheet is open, we want the ilot to be visible in the remaining map area
  // (not hidden behind the bottom sheet). We do the zoom again with padding.
  useEffect(() => {
    if (!opened || !selectedIlotFeature || !mapRef.current) {
      return;
    }

    const map = mapRef.current.getMap();
    const drawerPx =
      measuredDrawerHeight > 0
        ? measuredDrawerHeight
        : getPointDrawerMaxHeightPx();
    const paddingTop = 20;
    const paddingBottom = drawerPx + 20;

    const [x1, y1, x2, y2] = bbox(selectedIlotFeature.geometry);
    map.fitBounds([x1, y1, x2, y2], {
      padding: {
        top: paddingTop,
        bottom: paddingBottom,
        left: 80,
        right: 80,
      },
      animate: false,
    });
  }, [measuredDrawerHeight, opened, selectedIlotFeature]);

  return (
    <FullMainHeight.Root>
      <FullMainHeight.Content>
        <Paper
          className={clsx(styles['map-container'], 'border-light')}
          id="fields-map-container"
          p={0}
          pos="relative"
          shadow="sm"
        >
          <SegmentedControl
            className={styles.scopeFilter}
            color="pf-green"
            data={[
              { label: t('map.pointScope.personal'), value: 'personal' },
              {
                label: t('map.pointScope.organization'),
                value: 'organization',
              },
            ]}
            onChange={handleScopeChange}
            size="xs"
            value={scope}
          />
          <PfMap
            interactiveLayerIds={INTERACTIVE_LAYER_IDS}
            mapStyle="mapbox://styles/mapbox/satellite-streets-v12"
            onClick={handleMapClick}
            onLoad={handleLoad}
            ref={mapRef}
          >
            <StyleControl position="top-left" />

            <GeolocateControl
              geolocateOnInit
              onPositionChange={handlePositionChange}
              position="top-right"
            />

            <FieldsInBounds
              plotmapBbox={maxBounds.data?.bbox ?? null}
              scope={scope}
              withSatelliteControl={false}
            />
            {selectedIlotFeature ? (
              <PlotOutlineLayer
                data={selectedIlotFeature}
                lineColor={selectedIlotOutlineColor}
                lineWidth={4}
              />
            ) : null}
            {flattenedPoints.length > 0 ? (
              <IlotPointsLayer
                activePointCode={selectedPoint?.properties.code ?? null}
                activeSampleId={selectedSampleId}
                onPointClick={handlePointClick}
                points={flattenedPoints}
                size={28}
                withLabel
              />
            ) : null}

            <BottomSheet
              classNames={{
                body: clsx(styles.p0, styles.drawerBodyFit),
                content: styles.drawerContent,
                header: styles.drawerHeaderNoBg,
                root: styles.drawerRootInMap,
              }}
              lockScroll
              onClose={handleClose}
              opened={opened && selectedPoint !== null}
              portalProps={{ target: '#fields-map-container' }}
              withCloseButton={false}
            >
              <Box className={styles.pointDrawerBody}>
                {selectedPoint ? (
                  <Box className={styles.pointDrawerLayout}>
                    <Box className={styles.pointDrawerHeader}>
                      <Group
                        className={styles.pointDrawerHeaderActions}
                        grow
                        justify="space-between"
                        wrap="nowrap"
                      >
                        <ActionIcon
                          aria-label={t('sampling.map.aria.openDirections')}
                          className={styles.pointDrawerHeaderAction}
                          color="dark"
                          onClick={handleOpenTravelPlan}
                          radius="xl"
                          size={44}
                          variant="light"
                        >
                          <Navigation size={20} />
                        </ActionIcon>
                        <ActionIcon
                          aria-label={t('sampling.map.aria.openInfo')}
                          className={styles.pointDrawerHeaderAction}
                          color="dark"
                          onClick={handleOpenIlotInfo}
                          radius="xl"
                          size={44}
                          variant="light"
                        >
                          <Info size={20} />
                        </ActionIcon>
                        <ActionIcon
                          aria-label={t('sampling.map.aria.openNotes')}
                          className={styles.pointDrawerHeaderAction}
                          color="dark"
                          disabled={!selectedPointApiId}
                          onClick={handleOpenPointNotes}
                          radius="xl"
                          size={44}
                          variant="light"
                        >
                          <MessageSquare size={20} />
                        </ActionIcon>
                        <ActionIcon
                          aria-label={t('sampling.map.aria.close')}
                          className={styles.pointDrawerHeaderAction}
                          color="dark"
                          onClick={handleClose}
                          radius="xl"
                          size={44}
                          variant="light"
                        >
                          <XIcon size={20} />
                        </ActionIcon>
                      </Group>

                      <Group className={styles.pointDrawerMeta} gap={10} wrap="nowrap">
                        <Text className={styles.pointDrawerPointLabel} size="md">
                          {t('sampling.map.pointTitle', {
                            label: selectedPoint.properties.label,
                          })}
                        </Text>

                        <CopyButton
                          timeout={2000}
                          value={selectedPoint.properties.code}
                        >
                          {({ copied, copy }) => (
                            <UnstyledButton
                              aria-label={t('sampling.map.aria.copySampleCode')}
                              className={clsx(styles.pointDrawerCodeChip, {
                                [styles.pointDrawerCodeChipCopied]: copied,
                              })}
                              onClick={copy}
                            >
                              <Text
                                className={styles.pointDrawerSampleCode}
                                component="span"
                                ff="monospace"
                                size="sm"
                              >
                                {selectedPoint.properties.code}
                              </Text>
                              <span className={styles.pointDrawerCodeChipIcon}>
                                {copied ? (
                                  <Check size={16} strokeWidth={2.5} />
                                ) : (
                                  <Copy size={16} strokeWidth={2} />
                                )}
                              </span>
                            </UnstyledButton>
                          )}
                        </CopyButton>
                      </Group>
                    </Box>

                    <Box className={styles.pointDrawerFormArea}>
                      <Box
                        aria-disabled={isImpossible}
                        className={clsx(styles.pointDrawerFormContent, {
                          [styles.pointDrawerFormContentLocked]: isImpossible,
                        })}
                      >
                        <Box className={styles.pointDrawerScroll}>
                        <Stack gap="sm">
                          <TextInput
                            disabled={isImpossible}
                            label={
                              selectedPoint.properties.laboratory_name ? (
                                <>
                                  {t('sampling.map.sachetIdLabel')}{' '}
                                  <Text component="span" fw={700}>
                                    {selectedPoint.properties.laboratory_name}
                                  </Text>
                                </>
                              ) : (
                                t('sampling.map.sachetIdLabel')
                              )
                            }
                            onChange={(e) =>
                              setIdentifiantValue(e.currentTarget.value)
                            }
                            placeholder={t('sampling.map.sachetIdPlaceholder')}
                            radius="md"
                            size="sm"
                            rightSection={
                              <ActionIcon
                                aria-label={t('sampling.map.aria.scanBarcode')}
                                color={isScanningBarcode ? 'dark' : hasIdentifiantValue ? 'green' : 'dark'}
                                disabled={isImpossible}
                                onClick={isScanningBarcode ? stopBarcodeScan : startBarcodeScan}
                                radius="xl"
                                size={28}
                                variant="light"
                              >
                                {isScanningBarcode ? (
                                  <XIcon size={18} />
                                ) : (
                                  <ScanBarcode
                                    size={18}
                                  />
                                )}
                              </ActionIcon>
                            }
                            value={identifiantValue}
                          />

                          {barcodeScanError ? (
                            <Text c="red" size="sm">
                              {barcodeScanError}
                            </Text>
                          ) : null}

                          <Box className={styles.stoninessSelectRow}>
                            <Select
                              className={styles.stoninessSelect}
                              data={
                                pointsStoninessValues.data?.stoniness.map((s) => ({
                                  value: s.value,
                                  label: s.label,
                                })) ?? []
                              }
                              disabled={!isCloseEnough || isSampled || isImpossible}
                              label={t('sampling.map.stoniness')}
                              onChange={(value) => setStoninessValue(value)}
                              placeholder={t('sampling.map.stoninessPlaceholder')}
                              radius="md"
                              searchable={false}
                              size="sm"
                              value={stoninessValue}
                            />

                            <Group className={styles.stoninessActions} gap={6} wrap="nowrap">
                              <ActionIcon
                                aria-label={t('sampling.map.aria.takePhoto')}
                                color="dark"
                                disabled={
                                  !isCloseEnough ||
                                  isSampled ||
                                  isImpossible ||
                                  !selectedPointApiId ||
                                  uploadStoninessImageMutation.isPending
                                }
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  openStoninessImagePicker();
                                }}
                                radius="xl"
                                size={28}
                                variant="light"
                              >
                                {uploadStoninessImageMutation.isPending ? (
                                  <Loader size="xs" />
                                ) : (
                                  <Camera color="currentColor" size={16} />
                                )}
                              </ActionIcon>

                              <ActionIcon
                                aria-label={t('sampling.map.aria.viewImage')}
                                color={
                                  isStoninessImageUploaded ? 'green' : 'dark'
                                }
                                disabled={
                                  !stoninessImageUrl ||
                                  uploadStoninessImageMutation.isPending
                                }
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (!stoninessImageUrl) return;
                                    setStoninessImageModalUrl(stoninessImageUrl);
                                    setStoninessImageModalLoading(true);
                                    setStoninessImageModalOpened(true);
                                }}
                                radius="xl"
                                size={28}
                                variant="light"
                              >
                                <ImageIcon color="currentColor" size={18} />
                              </ActionIcon>
                            </Group>
                          </Box>

                          <input
                            accept="image/*"
                            capture="environment"
                            disabled={isImpossible || isSampled}
                            onChange={handleStoninessImageFileChange}
                            ref={stoninessImageInputRef}
                            style={{ display: 'none' }}
                            type="file"
                          />

                          {uploadStoninessImageMutation.isPending ? (
                            <Text c="dimmed" size="xs">
                              {t('sampling.map.uploadingPhoto')}
                            </Text>
                          ) : null}

                          {pointUpdateError ? (
                            <Text c="red" size="sm">
                              {pointUpdateError}
                            </Text>
                          ) : null}
                        </Stack>
                        </Box>

                        <Box className={styles.pointDrawerFooter}>
                          <Box className={styles.pointProgressRow} mb="xs">
                            <MapPin color={proximityUi.color} size={16} />
                            <Progress
                              styles={{
                                root: {
                                  flex: 1,
                                  backgroundColor: '#e9ecef',
                                },
                                section: { backgroundColor: proximityUi.color },
                              }}
                              value={proximityUi.value}
                            />
                            <Flag color={proximityUi.color} size={16} />
                          </Box>
                          {showValidationHint ? (
                            <Text
                              c={isSampled ? 'green' : canValidatePoint ? 'teal' : 'orange'}
                              mb={6}
                              size="xs"
                            >
                              {validationHint}
                            </Text>
                          ) : null}
                          <Group grow>
                            <Button
                              className={styles.pointActionButton}
                              color="red"
                              disabled={isSampled || isImpossible}
                              onClick={handleOpenPointImpossible}
                              radius="md"
                              variant="light"
                            >
                              {isSampled
                                ? t('sampling.map.buttons.alreadySampled')
                                : t('sampling.map.buttons.impossible')}
                            </Button>
                            <Button
                              className={styles.pointActionButton}
                              color={isSampled ? 'green' : canValidatePoint ? 'teal' : 'gray'}
                              disabled={isSampled || isImpossible || !canValidatePoint || validatePointMutation.isPending}
                              leftSection={
                                isSampled ? (
                                  <CheckCircle2 size={16} />
                                ) : canValidatePoint ? null : (
                                  <AlertCircle size={16} />
                                )
                              }
                              loading={validatePointMutation.isPending}
                              onClick={handleValidatePointClick}
                              radius="md"
                              variant="filled"
                            >
                              {isSampled
                                ? t('sampling.map.buttons.validated')
                                : isImpossible
                                  ? t('sampling.map.buttons.impossible')
                                : canValidatePoint
                                  ? t('sampling.map.buttons.validate')
                                  : t('sampling.map.buttons.validationUnavailable')}
                            </Button>
                          </Group>
                        </Box>
                      </Box>

                      {isImpossible ? (
                        <Box
                          aria-label={t('sampling.map.impossibleLocked')}
                          className={styles.pointDrawerLockedOverlay}
                          role="status"
                        >
                          <AlertCircle
                            aria-hidden
                            className={styles.pointDrawerLockedIcon}
                            size={28}
                          />
                          <Text fw={600} size="sm" ta="center">
                            {t('sampling.map.impossibleLocked')}
                          </Text>
                          {impossibleTitleLabel ? (
                            <Text c="dimmed" size="sm" ta="center">
                              {t('sampling.map.impossibleLockedReason', {
                                title: impossibleTitleLabel,
                              })}
                            </Text>
                          ) : null}
                        </Box>
                      ) : null}
                    </Box>
                  </Box>
                ) : null}
              </Box>
            </BottomSheet>
          </PfMap>
        </Paper>

        {isScanningBarcode ? (
          <Box
            aria-modal
            className={styles.barcodeScannerOverlay}
            role="dialog"
          >
            <Box className={styles.barcodeScannerOverlayTopBar}>
              <ActionIcon
                aria-label={t('sampling.map.aria.closeScan')}
                color="gray"
                onClick={stopBarcodeScan}
                radius="xl"
                size={40}
                variant="light"
              >
                <XIcon size={18} />
              </ActionIcon>
            </Box>

            <Box className={styles.barcodeScannerOverlayVideoWrap}>
              <video
                className={styles.barcodeScannerOverlayVideo}
                muted
                playsInline
                ref={videoRef}
              />
              <Box
                aria-hidden
                className={styles.barcodeScannerFrame}
              />
            </Box>

            <Box className={styles.barcodeScannerOverlayBottom}>
              {barcodeScanError ? (
                <Text c="red" mb={8} size="sm">
                  {barcodeScanError}
                </Text>
              ) : null}
              <Text c="dimmed" size="xs">
                {t('sampling.map.barcodeHint')}
              </Text>
            </Box>
          </Box>
        ) : null}

        <Modal
          centered
          onClose={handleCancelBarcodeConfirm}
          opened={pendingBarcodeValue !== null}
          padding="md"
          radius="md"
          title={t('sampling.map.barcodeConfirmTitle')}
          withCloseButton
        >
          <Stack gap="md">
            <Text c="dimmed" size="sm">
              {t('sampling.map.barcodeConfirmMessage')}
            </Text>
            <Paper p="md" radius="md" withBorder>
              <Text ff="monospace" fw={600} size="lg" ta="center">
                {formatSachetBarcodeDisplay(pendingBarcodeValue ?? '')}
              </Text>
            </Paper>
            <Stack gap="sm">
              <Button
                fullWidth
                loading={isConfirmingBarcode}
                onClick={handleConfirmBarcodeScan}
              >
                {t('sampling.map.barcodeConfirm')}
              </Button>
              <Button
                disabled={isConfirmingBarcode}
                fullWidth
                onClick={handleRescanBarcode}
                variant="default"
              >
                {t('sampling.map.barcodeRescan')}
              </Button>
            </Stack>
          </Stack>
        </Modal>

        <Modal
          centered
          onClose={() => setMissingPhotoConfirmOpened(false)}
          opened={missingPhotoConfirmOpened}
          padding="md"
          radius="md"
          title={t('sampling.map.missingPhotoConfirmTitle')}
          withCloseButton
        >
          <Stack gap="md">
            <Stack gap={6}>
              <Text c="orange" size="sm">
                {t('sampling.map.missingPhotoConfirmMessage')}
              </Text>
              <Text size="sm">
                {t('sampling.map.missingPhotoConfirmHint')}
              </Text>
            </Stack>
            <Stack gap="sm">
              <Button
                color="orange"
                fullWidth
                loading={validatePointMutation.isPending}
                onClick={submitPointValidation}
              >
                {t('sampling.map.missingPhotoConfirm')}
              </Button>
              <Button
                disabled={validatePointMutation.isPending}
                fullWidth
                onClick={() => setMissingPhotoConfirmOpened(false)}
                variant="default"
              >
                {t('sampling.map.missingPhotoCancel')}
              </Button>
            </Stack>
          </Stack>
        </Modal>

        <Modal
          centered
          onClose={() => setStoninessImageModalOpened(false)}
          opened={stoninessImageModalOpened}
          padding="md"
          radius="md"
          size="lg"
          title={t('sampling.map.stoninessPhotoTitle')}
          withCloseButton
        >
          {stoninessImageModalLoading ? (
            <Center style={{ minHeight: 160 }}>
              <Loader />
            </Center>
          ) : null}

          {stoninessImageModalUrl ? (
            <img
              alt="stoniness"
              onError={() => setStoninessImageModalLoading(false)}
              onLoad={() => setStoninessImageModalLoading(false)}
              src={stoninessImageModalUrl}
              style={{
                display: 'block',
                maxWidth: '100%',
                height: 'auto',
                margin: '0 auto',
              }}
            />
          ) : null}
        </Modal>

      </FullMainHeight.Content>
    </FullMainHeight.Root>
  );
}

FieldsMap.Error = ErrorPage;

export default FieldsMap;
